// React and React Router imports
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AdminSessionModel } from "features/admin/core/domain/admin-session.model";

// Material UI Core Components
import {
  Box,
  Button,
  Paper,
  Fade,
  Step,
  StepLabel,
  Stepper,
  Typography,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  TextareaAutosize,
  SelectChangeEvent,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  CircularProgress,
} from "@mui/material";

// Material UI Icons
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventNoteIcon from "@mui/icons-material/EventNote";
import TodayIcon from "@mui/icons-material/Today";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { FcCheckmark } from "react-icons/fc";

// Redux imports
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  dailyStaticData,
  selectDailyStaticData,
  fetchUserStores,
  setSelectedStore,
} from "../slices/daily-static-data.slice";
import { formList } from "../slices/form-list.slice";

// Custom components
import TransferList from "../components/transferlist";
import NumberInput from "../components/ms6-increment-decrement";
import StockoutInput from "../components/ms6-stockout-input";

// Utilities
import { dateSetup } from "features/stock-ordering/presentation/components/stock-ordering-utils";
import { StringNullableChain } from "lodash";
import { dailyProduct } from "../slices/daily-product-data.slice";

// Define types for form data
export interface AttendanceFormRow {
  id: string;
  store_code: string;
  attendance_date: string;
  shift: string;
  employee_name: string;
  attendance: string;
}

export interface IncidentFormRow {
  id: string;
  store_code: string;
  dateTimeReported: string;
  reportedTo: string;
  dateTimeOfIncident: string;
  reportedBy: string;
  peopleInvolved: string;
  actionTaken: string;
  remarks: string;
  status: string;
  employee_name: string;
}

export interface InventoryFormRow {
  id: string;
  store_code: string;
  date: string;
  itemDescription: string;
  criticalLevel: string;
  quantity: string;
  expiryDate: string;
}

export interface EfficiencyFormRow {
  id: string;
  store_code: string;
  date: string;
  shift: string;
  accountable: string;
  itemStockout: string;
  itemDescription: string;
  correctiveAction: string;
  preventiveAction: string;
}

export interface SvrFormRow {
  id: string;
  store_code: string;
  date: string;
  shift: string;
  cashier: string;
  declaredCash: string;
  calculatedCash: string;
  netSpecialSales: string;
  cashOverShort: string;
  dateTransaction: string;
  cashDeposit: string;
  varianceDeclaredDeposited: string;
  otherDeposits: string;
  creditCardSales: string;
  creditCardCharge: string;
  crMemo: string;
  gc: string;
  paymaya: string;
  vipSold: string;
  vip: string;
  centuryShopaholicVouchers: string;
  gcash: string;
  foodPanda: string;
  honestBee: string;
  metrodealNet: string;
  marketing: string;
  other: string;
  lazada: string;
  shopee: string;
  grab: string;
  booky: string;
  poodtrip: string;
  pickaroo: string;
  parahero: string;
  rareFoodShop: string;
  shopeePay: string;
  metroMart: string;
  zalora: string;
  eatigo: string;
  madison: string;
  smOnline: string;
  arExe: string;
  robinsons: string;
  catering: string;
  snackshop: string;
  reseller: string;
  arOps: string;
  arsmLifestyle: string;
  arBellevuehotel: string;
  offsite: string;
  happyappWallet: string;
  hyperMart: string;
  leePlaza: string;
  uymatiaoShipping: string;
  allEasy: string;
  arAct: string;
  shareTreat: string;
  totalnonCash: string;
  totalPayments: string;
  bulkWholesale: string;
  othersSpecify: string;
  cateringManual: string;
  offsiteSelling: string;
  resellerManual: string;
  snackshopManual: string;
  deliveryfeeManual: string;
  cartSales: string;
  consignment: string;
  totalSpecialSales: string;
  overallSales: string;
  srCitizen: string;
  pwdDisc: string;
  otherDisc: string;
  transactionCountPos: string;
  transactionValue: string;
  gcOriginatingStore: string;
  gcSerialNumber: string;
  terminalId: string;
  voids: string;
}

// Define type for input events
type InputChangeEvent = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

// Define calendar event types
type EventType = "Daily" | "Weekly" | "Monthly";

// Calendar event interface
interface CalendarEvent {
  id: string;
  title: EventType;
  date: Date;
  color: string;
  recurring: boolean;
}

// Day interface for calendar
interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  events: EventType[];
}

// Steps for the stepper
const dailySteps = [
  "Attendance Form",
  "Incident Reports",
  "Inventory Levels",
  "Efficiency Metrics",
  "Svr",
];
const weeklySteps = ["Weekly Report 1"];
const monthlySteps = ["Monthly Report 1"];

// Day names for calendar header
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function FormList(): React.ReactElement {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const attendanceStaticDataState = useAppSelector(selectDailyStaticData);
  const { stores, selectedStore } = attendanceStaticDataState; // Get stores and selectedStore from slice

  const [activeStep, setActiveStep] = useState<number>(0);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [showForms, setShowForms] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [dateWasSelected, setDateWasSelected] = useState<boolean>(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [incidentValidation, setIncidentValidation] = useState<
    Record<string, boolean>
  >({});
  const [inventoryValidation, setInventoryValidation] = useState<
    Record<string, boolean>
  >({});
  const [efficiencyValidation, setEfficiencyValidation] = useState<
    Record<string, boolean>
  >({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dateSelectionAlert, setDateSelectionAlert] = useState<string | null>(
    null
  );
  const [viewRecordsDialogOpen, setViewRecordsDialogOpen] =
    useState<boolean>(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertTitle, setAlertTitle] = React.useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Add these new state variables after existing ones
  const [todayStats, setTodayStats] = useState({
    totalEvents: 0,
    completedForms: 0,
    pendingForms: 0,
  });

  const [monthStats, setMonthStats] = useState({
    dailyEvents: 0,
    weeklyEvents: 0,
    monthlyEvents: 0,
  });

  // Generate a unique ID for new rows
  const generateUniqueId = () =>
    `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const { attendance_status_data, attendance_shift_data, employee_data } =
    attendanceStaticDataState.data?.attendanceData ?? {
      attendance_status_data: [],
      attendance_shift_data: [],
      employee_data: [],
    };

  // Fetch stores on mount
  useEffect(() => {
    dispatch(fetchUserStores());
  }, [dispatch]);

  // Fetch static data when store changes
  useEffect(() => {
    if (selectedStore && selectedEvent === "Daily") {
      dispatch(dailyStaticData(selectedStore));
    }
  }, [selectedStore, selectedEvent, dispatch]);

  // Attendance form rows state
  const [attendanceRows, setAttendanceRows] = useState<AttendanceFormRow[]>([
    {
      id: generateUniqueId(),
      store_code: "",
      attendance_date: "",
      shift: "",
      employee_name: "",
      attendance: "",
    },
  ]);

  useEffect(() => {
    if (!showForms) {
      setAttendanceRows([
        {
          id: generateUniqueId(),
          store_code: "",
          attendance_date: "",
          shift: "",
          employee_name: "",
          attendance: "",
        },
      ]);
    }
  }, [showForms]);

  useEffect(() => {
    if (attendanceStaticDataState.data && selectedStore) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Reset attendanceRows with the new date and employee data
      const fillEmp = employee_data.map((emp) => ({
        id: generateUniqueId(),
        store_code: selectedStore, // Use selectedStore
        attendance_date: formattedDate,
        shift: "",
        employee_name: emp.id.toString(),
        attendance: "",
      }));

      setAttendanceRows(fillEmp);
    }
  }, [
    attendanceStaticDataState.data,
    selectedDate,
    employee_data,
    selectedStore,
    dispatch,
  ]);

  // Incident form rows state
  const [incidentRows, setIncidentRows] = useState<IncidentFormRow[]>([
    {
      id: generateUniqueId(),
      store_code: "",
      dateTimeReported: "",
      reportedTo: "",
      dateTimeOfIncident: "",
      reportedBy: "",
      peopleInvolved: "",
      actionTaken: "",
      remarks: "",
      status: "",
      employee_name: "",
    },
  ]);

  useEffect(() => {
    if (attendanceStaticDataState.data && selectedStore) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Update all rows with the new date
      const updatedRows = incidentRows.map((row) => ({
        ...row,
        store_code: selectedStore, // Use selectedStore
        dateTimeReported: formattedDate,
      }));

      setIncidentRows(updatedRows);
    }
  }, [attendanceStaticDataState.data, selectedDate, selectedStore]);

  const [inventoryRows, setInventoryRows] = useState<InventoryFormRow[]>([
    {
      id: generateUniqueId(),
      store_code: "",
      date: "",
      itemDescription: "",
      criticalLevel: "",
      quantity: "",
      expiryDate: "",
    },
  ]);

  useEffect(() => {
    if (attendanceStaticDataState.data && selectedStore) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Update all rows with the new date
      const updatedRows = inventoryRows.map((row) => ({
        ...row,
        store_code: selectedStore, // Use selectedStore
        date: formattedDate,
      }));

      setInventoryRows(updatedRows);
    }
  }, [attendanceStaticDataState.data, selectedDate, selectedStore]);

  const [efficiencyRows, setEfficiencyRows] = useState<EfficiencyFormRow[]>([
    {
      id: generateUniqueId(),
      store_code: "",
      date: "",
      shift: "",
      accountable: "",
      itemStockout: "",
      itemDescription: "",
      correctiveAction: "",
      preventiveAction: "",
    },
  ]);

  useEffect(() => {
    dispatch(dailyProduct());
  }, [dispatch]);

  useEffect(() => {
    if (attendanceStaticDataState.data && selectedStore) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Update all rows with the new date
      const updatedRows = efficiencyRows.map((row) => ({
        ...row,
        store_code: selectedStore, // Use selectedStore
        date: formattedDate,
      }));

      setEfficiencyRows(updatedRows);
    }
  }, [attendanceStaticDataState.data, selectedDate, selectedStore]);

  const [svrRows, setSvrRows] = useState<SvrFormRow[]>([
    {
      id: generateUniqueId(),
      store_code: "",
      date: "",
      shift: "",
      cashier: "",
      declaredCash: "",
      calculatedCash: "",
      netSpecialSales: "",
      cashOverShort: "",
      dateTransaction: "",
      cashDeposit: "",
      varianceDeclaredDeposited: "",
      otherDeposits: "",
      creditCardSales: "",
      creditCardCharge: "",
      crMemo: "",
      gc: "",
      paymaya: "",
      vipSold: "",
      vip: "",
      centuryShopaholicVouchers: "",
      gcash: "",
      foodPanda: "",
      honestBee: "",
      metrodealNet: "",
      marketing: "",
      other: "",
      lazada: "",
      shopee: "",
      grab: "",
      booky: "",
      poodtrip: "",
      pickaroo: "",
      parahero: "",
      rareFoodShop: "",
      shopeePay: "",
      metroMart: "",
      zalora: "",
      eatigo: "",
      madison: "",
      smOnline: "",
      arExe: "",
      robinsons: "",
      catering: "",
      snackshop: "",
      reseller: "",
      arOps: "",
      arsmLifestyle: "",
      arBellevuehotel: "",
      offsite: "",
      happyappWallet: "",
      hyperMart: "",
      leePlaza: "",
      uymatiaoShipping: "",
      allEasy: "",
      arAct: "",
      shareTreat: "",
      totalnonCash: "",
      totalPayments: "",
      bulkWholesale: "",
      othersSpecify: "",
      cateringManual: "",
      offsiteSelling: "",
      resellerManual: "",
      snackshopManual: "",
      deliveryfeeManual: "",
      cartSales: "",
      consignment: "",
      totalSpecialSales: "",
      overallSales: "",
      srCitizen: "",
      pwdDisc: "",
      otherDisc: "",
      transactionCountPos: "",
      transactionValue: "",
      gcOriginatingStore: "",
      gcSerialNumber: "",
      terminalId: "",
      voids: "",
    },
  ]);

  useEffect(() => {
    if (attendanceStaticDataState.data && selectedStore) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Update all rows with the new date
      const updatedRows = svrRows.map((row) => ({
        ...row,
        store_code: selectedStore, // Use selectedStore
        date: formattedDate,
      }));

      setSvrRows(updatedRows);
    }
  }, [attendanceStaticDataState.data, selectedDate, selectedStore]);

  // Add this useEffect to calculate statistics
  useEffect(() => {
    const today = new Date();
    const currentMonthEvents = getDaysForMonthView().filter(
      (day) => day.isCurrentMonth && day.events.length > 0
    );

    const dailyCount = currentMonthEvents.filter((day) =>
      day.events.includes("Daily")
    ).length;

    const weeklyCount = currentMonthEvents.filter((day) =>
      day.events.includes("Weekly")
    ).length;

    const monthlyCount = currentMonthEvents.filter((day) =>
      day.events.includes("Monthly")
    ).length;

    setMonthStats({
      dailyEvents: dailyCount,
      weeklyEvents: weeklyCount,
      monthlyEvents: monthlyCount,
    });

    // Today's events
    const todayEvents = getEventsForDate(today);
    setTodayStats({
      totalEvents: todayEvents.length,
      completedForms: 0,
      pendingForms: todayEvents.length,
    });
  }, [currentMonth]);

  // Initialize the transfer list states
  const [leftItems, setLeftItems] = useState<string[]>([
    "netSpecialSales",
    "cashDeposit",
    "otherDeposits",
    "creditCardSales",
    "creditCardCharge",
    "crMemo",
    "gc",
    "paymaya",
    "vipSold",
    "vip",
    "centuryShopaholicVouchers",
    "gcash",
    "foodPanda",
    "honestBee",
    "metrodealNet",
    "marketing",
    "other",
    "lazada",
    "shopee",
    "grab",
    "booky",
    "poodtrip",
    "pickaroo",
    "parahero",
    "rareFoodShop",
    "shopeePay",
    "metroMart",
    "zalora",
    "eatigo",
    "madison",
    "smOnline",
    "arExe",
    "robinsons",
    "catering",
    "snackshop",
    "reseller",
    "arOps",
    "arsmLifestyle",
    "arBellevuehotel",
    "offsite",
    "happyappWallet",
    "hyperMart",
    "leePlaza",
    "uymatiaoShipping",
    "allEasy",
    "arAct",
    "shareTreat",
    "totalnonCash",
    "bulkWholesale",
    "othersSpecify",
    "cateringManual",
    "offsiteSelling",
    "resellerManual",
    "snackshopManual",
    "deliveryfeeManual",
    "cartSales",
    "consignment",
    "totalSpecialSales",
    "srCitizen",
    "pwdDisc",
    "otherDisc",
    "transactionCountPos",
    "gcOriginatingStore",
    "gcSerialNumber",
    "terminalId",
    "voids",
  ]);

  const [rightItems, setRightItems] = useState<string[]>([]);

  // Handler for the transfer list changes
  const handleTransferListChange = (newLeft: string[], newRight: string[]) => {
    setLeftItems(newLeft);
    setRightItems(newRight);
  };

  // Calendar events
  const [events, setEvents] = useState<CalendarEvent[]>([
    {
      id: generateUniqueId(),
      title: "Daily",
      date: new Date(),
      color: "#800000",
      recurring: true,
    },
    {
      id: generateUniqueId(),
      title: "Weekly",
      date: new Date(),
      color: "#0F9D58",
      recurring: true,
    },
    {
      id: generateUniqueId(),
      title: "Monthly",
      date: new Date(),
      color: "#DC0000",
      recurring: true,
    },
  ]);

  const renderStoreSelector = () => {
    if (stores.length <= 1) {
      return null;
    }

    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: "1.5rem",
            color: "#2c3e50",
            textAlign: "center",
            mb: 2,
          }}
        >
          Select Store
        </Typography>

        <Grid container justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="store-select-label">Choose Store</InputLabel>
              <Select
                labelId="store-select-label"
                value={selectedStore}
                label="Choose Store"
                onChange={(e) => dispatch(setSelectedStore(e.target.value))}
                sx={{
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#800000",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#800000",
                  },
                }}
              >
                {stores.map((store) => (
                  <MenuItem key={store.store_id} value={store.store_id}>
                    {store.store_name || store.store_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {selectedStore && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Managing forms for:{" "}
              <strong>
                {stores.find((s) => s.store_id === selectedStore)?.store_name ||
                  selectedStore}
              </strong>
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Get current steps based on selected event type
  const getCurrentSteps = (): string[] => {
    switch (selectedEvent) {
      case "Daily":
        return dailySteps;
      case "Weekly":
        return weeklySteps;
      case "Monthly":
        return monthlySteps;
      default:
        return dailySteps;
    }
  };

  // Get days in month
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get days for current month view
  const getDaysForMonthView = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const days: CalendarDay[] = [];

    // Previous month days to fill first week
    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthDays - i);
      days.push({
        date,
        isCurrentMonth: false,
        events: getEventsForDate(date),
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        date,
        isCurrentMonth: true,
        events: getEventsForDate(date),
      });
    }

    // Next month days to fill last week
    const remainingDays = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        date,
        isCurrentMonth: false,
        events: getEventsForDate(date),
      });
    }

    return days;
  };

  // Get events for a specific date
  const getEventsForDate = (date: Date): EventType[] => {
    const events: EventType[] = [];

    // Check for daily events (except Sundays but now including sunday)
    events.push("Daily");

    // Check for weekly events (every Monday)
    if (date.getDay() === 1) {
      events.push("Weekly");
    }

    // Check for monthly events (first Monday of the month)
    if (date.getDay() === 1 && date.getDate() <= 7) {
      events.push("Monthly");
    }

    return events;
  };

  // Handle date selection, when a user selects a date
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setDateWasSelected(true);
  };

  // Handle month navigation
  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Handle event selection
  const handleEventSelect = (event: EventType) => {
    if (!dateWasSelected) {
      setDateSelectionAlert("Please Select a Day First");
      setAlertOpen(true);
      setAlertTitle("Date Selection Required");
      setAlertMessage("Please select a date first.");
      return;
    }

    setDateSelectionAlert(null);
    setIsLoading(true);

    // Simulate loading delay
    setTimeout(() => {
      setSelectedEvent(event);
      setShowForms(true);
      setActiveStep(0);

      if (event === "Daily") {
        dispatch(dailyStaticData(selectedStore));
      }
      setIsLoading(false);
    }, 500);
  };

  // Handle attendance form input change
  const handleAttendanceInputChange = (
    id: string,
    e: InputChangeEvent
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setAttendanceRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle attendance form select change
  const handleAttendanceSelectChange = (
    id: string,
    e: SelectChangeEvent<any>
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setAttendanceRows((prev) =>
        prev.map((row) => {
          if (row.id === id) {
            const updatedRow = { ...row, [name]: value };

            if (
              name === "attendance" &&
              parseInt(value) >= 4 &&
              parseInt(value) <= 16
            ) {
              updatedRow.shift = "3";
            }

            return updatedRow;
          }
          return row;
        })
      );
    }
  };

  // Handle incident form input change
  const handleIncidentInputChange = (id: string, e: InputChangeEvent): void => {
    const { name, value } = e.target;
    if (name) {
      setIncidentRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle incident form select change
  const handleIncidentSelectChange = (
    id: string,
    e: SelectChangeEvent<any>
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setIncidentRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle incident inventory input change
  const handleInventoryInputChange = (
    id: string,
    e: InputChangeEvent
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setInventoryRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle incident form select change
  const handleInventorySelectChange = (
    id: string,
    e: SelectChangeEvent<any>
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setInventoryRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle efficiency input change
  const handleEfficiencyInputChange = (
    id: string,
    e: InputChangeEvent
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setEfficiencyRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle efficiency select change
  const handleEfficiencySelectChange = (
    id: string,
    e: SelectChangeEvent<any>
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setEfficiencyRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle svr input change
  const handleSvrInputChange = (id: string, e: InputChangeEvent): void => {
    const { name, value } = e.target;
    if (name) {
      setSvrRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Handle svr select change
  const handleSvrSelectChange = (
    id: string,
    e: SelectChangeEvent<any>
  ): void => {
    const { name, value } = e.target;
    if (name) {
      setSvrRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, [name]: value } : row))
      );
    }
  };

  // Add new attendance row
  const addAttendanceRow = (): void => {
    setAttendanceRows((prev) => [
      ...prev,
      {
        id: generateUniqueId(),
        store_code: selectedStore,
        attendance_date: "",
        shift: "defaultShift",
        employee_name: "",
        attendance: "defaultAttendance",
      },
    ]);
  };

  // Add new incident row
  const addIncidentRow = (): void => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    setIncidentRows((prev) => [
      ...prev,
      {
        id: generateUniqueId(),
        store_code: selectedStore,
        dateTimeReported: formattedDate,
        reportedTo: "",
        dateTimeOfIncident: "",
        reportedBy: "",
        peopleInvolved: "",
        actionTaken: "",
        remarks: "",
        status: "",
        employee_name: "",
      },
    ]);
  };

  // Add new inventory row
  const addInventoryRow = (): void => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    setInventoryRows((prev) => [
      ...prev,
      {
        id: generateUniqueId(),
        store_code: selectedStore,
        date: formattedDate,
        itemDescription: "",
        criticalLevel: "",
        quantity: "",
        expiryDate: "",
      },
    ]);
  };

  // Add new efficiency row
  const addEfficiencyRow = (): void => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    setEfficiencyRows((prev) => [
      ...prev,
      {
        id: generateUniqueId(),
        store_code: selectedStore,
        date: formattedDate,
        shift: "",
        accountable: "",
        itemStockout: "",
        itemDescription: "",
        correctiveAction: "",
        preventiveAction: "",
      },
    ]);
  };

  // Add new svr row
  const addSvrRow = (): void => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;

    const newRow: SvrFormRow = {
      id: generateUniqueId(),
      store_code: selectedStore,
      date: formattedDate,
      shift: "",
      cashier: "",
      declaredCash: "",
      calculatedCash: "",
      netSpecialSales: "",
      cashOverShort: "",
      dateTransaction: "",
      cashDeposit: "",
      varianceDeclaredDeposited: "",
      otherDeposits: "",
      creditCardSales: "",
      creditCardCharge: "",
      crMemo: "",
      gc: "",
      paymaya: "",
      vipSold: "",
      vip: "",
      centuryShopaholicVouchers: "",
      gcash: "",
      foodPanda: "",
      honestBee: "",
      metrodealNet: "",
      marketing: "",
      other: "",
      lazada: "",
      shopee: "",
      grab: "",
      booky: "",
      poodtrip: "",
      pickaroo: "",
      parahero: "",
      rareFoodShop: "",
      shopeePay: "",
      metroMart: "",
      zalora: "",
      eatigo: "",
      madison: "",
      smOnline: "",
      arExe: "",
      robinsons: "",
      catering: "",
      snackshop: "",
      reseller: "",
      arOps: "",
      arsmLifestyle: "",
      arBellevuehotel: "",
      offsite: "",
      happyappWallet: "",
      hyperMart: "",
      leePlaza: "",
      uymatiaoShipping: "",
      allEasy: "",
      arAct: "",
      shareTreat: "",
      totalnonCash: "",
      totalPayments: "",
      bulkWholesale: "",
      othersSpecify: "",
      cateringManual: "",
      offsiteSelling: "",
      resellerManual: "",
      snackshopManual: "",
      deliveryfeeManual: "",
      cartSales: "",
      consignment: "",
      totalSpecialSales: "",
      overallSales: "",
      srCitizen: "",
      pwdDisc: "",
      otherDisc: "",
      transactionCountPos: "",
      transactionValue: "",
      gcOriginatingStore: "",
      gcSerialNumber: "",
      terminalId: "",
      voids: "",
    };

    setSvrRows((prev) => [...prev, newRow]);
  };

  // Remove attendance row
  const removeAttendanceRow = (id: string): void => {
    if (attendanceRows.length > 1) {
      setAttendanceRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  // Remove incident row
  const removeIncidentRow = (id: string): void => {
    if (incidentRows.length > 1) {
      setIncidentRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  // Remove inventory row
  const removeInventoryRow = (id: string): void => {
    if (inventoryRows.length > 1) {
      setInventoryRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  // Remove efficiency row
  const removeEfficiencyRow = (id: string): void => {
    if (efficiencyRows.length > 1) {
      setEfficiencyRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  // Remove svr row
  const removeSvrRow = (id: string): void => {
    if (svrRows.length > 1) {
      setSvrRows((prev) => prev.filter((row) => row.id !== id));
    }
  };

  // Open confirmation dialog
  const openConfirmationDialog = (): void => {
    setConfirmDialogOpen(true);
  };

  // Handle form submission after confirmation
  const handleConfirmedSubmit = (): void => {
    const hasInvalidAttendance = attendanceRows.some((row) => {
      return (
        !row.attendance_date ||
        !row.attendance ||
        !row.employee_name ||
        !row.shift
      );
    });

    if (hasInvalidAttendance) {
      setAlertTitle("Attendance Form Error");
      setAlertMessage("Please fill all required fields in the Attendance Form");
      setAlertOpen(true);
      return;
    }

    const hasInvalidSvr = svrRows.some((row) => {
      return (
        !row.date ||
        !row.dateTransaction ||
        !row.shift ||
        !row.cashier ||
        !row.declaredCash ||
        !row.calculatedCash
      );
    });

    if (hasInvalidSvr) {
      setAlertTitle("SVR Form Error");
      setAlertMessage("Please fill all required fields in the SVR Form");
      setAlertOpen(true);
      return;
    }

    const filteredIncidentData = incidentRows.filter((row) => {
      const requireAll = shouldRequireFields(row);
      if (row.dateTimeReported && !requireAll) return false;
      return (
        !requireAll ||
        (row.dateTimeReported &&
          row.dateTimeOfIncident &&
          row.reportedTo &&
          row.reportedBy &&
          row.peopleInvolved &&
          row.actionTaken &&
          row.status &&
          row.remarks)
      );
    });

    const filteredInventoryData = inventoryRows.filter((row) => {
      const requireAll = shouldRequireInventoryFields(row);
      if (row.date && !requireAll) return false;
      return (
        !requireAll ||
        (row.date &&
          row.itemDescription &&
          row.criticalLevel &&
          row.quantity &&
          row.expiryDate)
      );
    });

    const filteredEfficiencyData = efficiencyRows.filter((row) => {
      const requireAll = shouldRequireEfficiencyFields(row);
      if (row.date && !requireAll) return false;
      return (
        !requireAll ||
        (row.date &&
          row.shift &&
          row.accountable &&
          row.itemStockout &&
          row.itemDescription &&
          row.correctiveAction &&
          row.preventiveAction)
      );
    });

    const hasInvalidIncidents = incidentRows.some((row) => {
      const requireAll = shouldRequireFields(row);
      return (
        requireAll &&
        !(
          row.dateTimeReported &&
          row.dateTimeOfIncident &&
          row.reportedTo &&
          row.reportedBy &&
          row.peopleInvolved &&
          row.actionTaken &&
          row.status &&
          row.remarks
        )
      );
    });

    const hasInvalidInventory = inventoryRows.some((row) => {
      const requireAll = shouldRequireInventoryFields(row);
      return (
        requireAll &&
        !(
          row.date &&
          row.itemDescription &&
          row.criticalLevel &&
          row.quantity &&
          row.expiryDate
        )
      );
    });

    const hasInvalidEfficiency = efficiencyRows.some((row) => {
      const requireAll = shouldRequireEfficiencyFields(row);
      return (
        requireAll &&
        !(
          row.date &&
          row.shift &&
          row.accountable &&
          row.itemStockout &&
          row.itemDescription &&
          row.correctiveAction &&
          row.preventiveAction
        )
      );
    });

    if (hasInvalidIncidents) {
      setAlertTitle("Incident Reports Error");
      setAlertMessage(
        "Please fill all required fields in the Incident Reports"
      );
      setAlertOpen(true);
      return;
    }

    if (hasInvalidInventory) {
      setAlertTitle("Inventory Levels Error");
      setAlertMessage(
        "Please fill all required fields in the Inventory Levels"
      );
      setAlertOpen(true);
      return;
    }

    if (hasInvalidEfficiency) {
      setAlertTitle("Efficiency Metrics Error");
      setAlertMessage(
        "Please fill all required fields in the Efficiency Metrics"
      );
      setAlertOpen(true);
      return;
    }

    // Calculate SVR values before submission
    const calculatedSvrRows = svrRows.map((row) => {
      const declaredCashNum = parseFloat(row.declaredCash) || 0;
      const calculatedCashNum = parseFloat(row.calculatedCash) || 0;
      const cashDepositNum = parseFloat(row.cashDeposit) || 0;
      const totalnonCashNum = parseFloat(row.totalnonCash) || 0;
      const totalSpecialSalesNum = parseFloat(row.totalSpecialSales) || 0;
      const transactionCountPosNum = parseFloat(row.transactionCountPos) || 0;

      const cashOverShort = (declaredCashNum - calculatedCashNum).toString();
      const varianceDeclaredDeposited = (
        cashDepositNum - declaredCashNum
      ).toString();
      const totalPayments = (totalnonCashNum + calculatedCashNum).toString();
      const overallSales = (
        totalSpecialSalesNum +
        totalnonCashNum +
        calculatedCashNum
      ).toString();
      const transactionValue =
        transactionCountPosNum !== 0
          ? (parseFloat(overallSales) / transactionCountPosNum).toString()
          : "0";

      return {
        ...row,
        cashOverShort: row.cashOverShort || cashOverShort,
        varianceDeclaredDeposited:
          row.varianceDeclaredDeposited || varianceDeclaredDeposited,
        totalPayments: row.totalPayments || totalPayments,
        overallSales: row.overallSales || overallSales,
        transactionValue: row.transactionValue || transactionValue,
      };
    });

    setConfirmDialogOpen(false);
    dispatch(
      formList({
        attendanceData: attendanceRows,
        incidentData: filteredIncidentData,
        inventoryData: filteredInventoryData,
        efficiencyData: filteredEfficiencyData,
        svrData: calculatedSvrRows,
        selectedStore: selectedStore, // Include selectedStore in submission
      })
    );

    console.log("Submitting attendance data:", attendanceRows);
    console.log("Submitting incident data:", incidentRows);
    console.log("Submitting inventory data:", inventoryRows);
    console.log("Submitting efficiency data:", efficiencyRows);
    console.log("Submitting svr data:", calculatedSvrRows);
    console.log("Event type:", selectedEvent);
    console.log("Selected date:", selectedDate);
    console.log("Selected store:", selectedStore);

    // Reset forms with selectedStore
    setAttendanceRows([
      {
        id: generateUniqueId(),
        store_code: selectedStore,
        attendance_date: "",
        shift: "AM",
        employee_name: "",
        attendance: "Present",
      },
    ]);

    setIncidentRows([
      {
        id: generateUniqueId(),
        store_code: selectedStore,
        dateTimeReported: "",
        reportedTo: "",
        dateTimeOfIncident: "",
        reportedBy: "",
        peopleInvolved: "",
        actionTaken: "",
        remarks: "",
        status: "",
        employee_name: "",
      },
    ]);

    setInventoryRows([
      {
        id: generateUniqueId(),
        store_code: selectedStore,
        date: "",
        itemDescription: "",
        criticalLevel: "",
        quantity: "",
        expiryDate: "",
      },
    ]);

    setSvrRows([
      {
        id: generateUniqueId(),
        store_code: selectedStore,
        date: "",
        shift: "",
        cashier: "",
        declaredCash: "",
        calculatedCash: "",
        netSpecialSales: "",
        cashOverShort: "",
        dateTransaction: "",
        cashDeposit: "",
        varianceDeclaredDeposited: "",
        otherDeposits: "",
        creditCardSales: "",
        creditCardCharge: "",
        crMemo: "",
        gc: "",
        paymaya: "",
        vipSold: "",
        vip: "",
        centuryShopaholicVouchers: "",
        gcash: "",
        foodPanda: "",
        honestBee: "",
        metrodealNet: "",
        marketing: "",
        other: "",
        lazada: "",
        shopee: "",
        grab: "",
        booky: "",
        poodtrip: "",
        pickaroo: "",
        parahero: "",
        rareFoodShop: "",
        shopeePay: "",
        metroMart: "",
        zalora: "",
        eatigo: "",
        madison: "",
        smOnline: "",
        arExe: "",
        robinsons: "",
        catering: "",
        snackshop: "",
        reseller: "",
        arOps: "",
        arsmLifestyle: "",
        arBellevuehotel: "",
        offsite: "",
        happyappWallet: "",
        hyperMart: "",
        leePlaza: "",
        uymatiaoShipping: "",
        allEasy: "",
        arAct: "",
        shareTreat: "",
        totalnonCash: "",
        totalPayments: "",
        bulkWholesale: "",
        othersSpecify: "",
        cateringManual: "",
        offsiteSelling: "",
        resellerManual: "",
        snackshopManual: "",
        deliveryfeeManual: "",
        cartSales: "",
        consignment: "",
        totalSpecialSales: "",
        overallSales: "",
        srCitizen: "",
        pwdDisc: "",
        otherDisc: "",
        transactionCountPos: "",
        transactionValue: "",
        gcOriginatingStore: "",
        gcSerialNumber: "",
        terminalId: "",
        voids: "",
      },
    ]);

    setSubmitted(true);
    setActiveStep(0);
    setShowForms(false);
    setSelectedEvent(null);
  };

  // Go to view page based on current case
  const goToViewPage = (): void => {
    setViewRecordsDialogOpen(true);
  };

  // Add a handler for confirming the navigation
  const handleViewRecordsConfirm = () => {
    const baseUrl = `${window.location.origin}${
      window.location.pathname.split("/admin")[0]
    }`;

    switch (activeStep) {
      case 0:
        window.open(`${baseUrl}/admin/ms6/manpower/daily-attendance`, "_blank");
        break;
      case 1:
        window.open(`${baseUrl}/admin/ms6/manpower/incident-report`, "_blank");
        break;
      case 2:
        window.open(`${baseUrl}/admin/ms6/material/inventory-levels`, "_blank");
        break;
      case 3:
        window.open(
          `${baseUrl}/admin/ms6/material/efficiency-metric`,
          "_blank"
        );
        break;
      case 4:
        window.open(`${baseUrl}/admin/ms6/money`, "_blank");
        break;
      default:
        window.open(`${baseUrl}/admin/ms6/manpower`, "_blank");
        break;
    }

    setViewRecordsDialogOpen(false);
  };

  // Handle next step
  const handleNext = (): void => {
    let hasErrors = false;
    let errorTitle = "";
    let errorMessage = "";

    switch (activeStep) {
      case 0:
        const hasInvalidAttendance = attendanceRows.some((row) => {
          return (
            !row.attendance_date ||
            !row.attendance ||
            !row.employee_name ||
            !row.shift
          );
        });
        if (hasInvalidAttendance) {
          hasErrors = true;
          errorTitle = "Attendance Form Error";
          errorMessage =
            "Please fill all required fields in the Attendance Form";
        }
        break;

      case 1:
        const hasInvalidIncidents = incidentRows.some((row) => {
          const requireAll = shouldRequireFields(row);
          return (
            requireAll &&
            !(
              row.dateTimeReported &&
              row.dateTimeOfIncident &&
              row.reportedTo &&
              row.reportedBy &&
              row.peopleInvolved &&
              row.actionTaken &&
              row.status &&
              row.remarks
            )
          );
        });
        if (hasInvalidIncidents) {
          hasErrors = true;
          errorTitle = "Incident Reports Error";
          errorMessage =
            "Please fill all required fields in the Incident Reports";
        }
        break;

      case 2:
        const hasInvalidInventory = inventoryRows.some((row) => {
          const requireAll = shouldRequireInventoryFields(row);
          return (
            requireAll &&
            !(
              row.date &&
              row.itemDescription &&
              row.criticalLevel &&
              row.quantity &&
              row.expiryDate
            )
          );
        });
        if (hasInvalidInventory) {
          hasErrors = true;
          errorTitle = "Inventory Levels Error";
          errorMessage =
            "Please fill all required fields in the Inventory Levels";
        }
        break;

      case 3:
        const hasInvalidEfficiency = efficiencyRows.some((row) => {
          const requireAll = shouldRequireEfficiencyFields(row);
          return (
            requireAll &&
            !(
              row.date &&
              row.shift &&
              row.accountable &&
              row.itemStockout &&
              row.itemDescription &&
              row.correctiveAction &&
              row.preventiveAction
            )
          );
        });
        if (hasInvalidEfficiency) {
          hasErrors = true;
          errorTitle = "Efficiency Metrics Error";
          errorMessage =
            "Please fill all required fields in the Efficiency Metrics";
        }
        break;

      case 4:
        const hasInvalidSvr = svrRows.some((row) => {
          return (
            !row.date ||
            !row.dateTransaction ||
            !row.shift ||
            !row.cashier ||
            !row.declaredCash ||
            !row.calculatedCash
          );
        });
        if (hasInvalidSvr) {
          hasErrors = true;
          errorTitle = "SVR Form Error";
          errorMessage = "Please fill all required fields in the SVR Form";
        }
        break;

      default:
        break;
    }

    if (hasErrors) {
      setAlertTitle(errorTitle);
      setAlertMessage(errorMessage);
      setAlertOpen(true);
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handle back step
  const handleBack = (): void => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Render confirmation dialog content
  const renderConfirmationDialogContent = (): React.ReactNode => {
    return (
      <>
        <DialogContentText>
          Please review the missing required fields before submitting:
        </DialogContentText>

        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: "bold" }}>
          Event Type: {selectedEvent}
        </Typography>
        <Typography variant="body2">
          Date: {selectedDate.toLocaleDateString()}
        </Typography>
        <Typography variant="body2">
          Store:{" "}
          {stores.find((s) => s.store_id === selectedStore)?.store_name ||
            selectedStore}
        </Typography>

        {/* Rest of your existing confirmation dialog content */}
        {selectedEvent === "Daily" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Attendance Form
            </Typography>
            {attendanceRows.every(
              (row) =>
                row.attendance_date &&
                row.attendance &&
                row.employee_name &&
                row.shift
            ) ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "success.main",
                }}
              >
                <FcCheckmark style={{ marginRight: 8 }} />
                <Typography>All required fields completed</Typography>
              </Box>
            ) : (
              <List dense>
                {attendanceRows.map((row, index) => {
                  const missingFields = [];
                  if (!row.attendance_date) missingFields.push("Date");
                  if (!row.attendance) missingFields.push("Attendance");
                  if (!row.employee_name) missingFields.push("Employee Name");
                  if (!row.shift) missingFields.push("Shift");

                  return missingFields.length > 0 ? (
                    <ListItem key={row.id}>
                      <ListItemText
                        primary={`Record ${index + 1}`}
                        secondary={`Missing: ${missingFields.join(", ")}`}
                      />
                    </ListItem>
                  ) : null;
                })}
              </List>
            )}
          </Box>
        )}

        {/* Add other form confirmations here - incident, inventory, efficiency, svr */}

        <DialogContentText sx={{ mt: 2 }}>
          {attendanceRows.every(
            (row) =>
              row.attendance_date &&
              row.attendance &&
              row.employee_name &&
              row.shift
          ) &&
          svrRows.every(
            (row) =>
              row.date &&
              row.dateTransaction &&
              row.shift &&
              row.cashier &&
              row.declaredCash &&
              row.calculatedCash
          )
            ? "All mandatory forms are complete. Would you like to submit?"
            : "Please fill in all required fields before submitting."}
        </DialogContentText>
      </>
    );
  };

  // Get content for current step based on event type
  const getStepContent = (step: number): React.ReactNode => {
    switch (selectedEvent) {
      case "Daily":
        return getDailyStepContent(step);
      case "Weekly":
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Weekly reports will be available in the future.
            </Typography>
            <Typography variant="body1">
              This feature is currently under development. Please check back
              later.
            </Typography>
          </Box>
        );
      case "Monthly":
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Monthly reports will be available in the future.
            </Typography>
            <Typography variant="body1">
              This feature is currently under development. Please check back
              later.
            </Typography>
          </Box>
        );
      default:
        return "Unknown step";
    }
  };

  // Helper functions for validation
  const shouldRequireFields = (row: IncidentFormRow) => {
    return (
      row.reportedTo !== "" ||
      row.dateTimeOfIncident !== "" ||
      row.reportedBy !== "" ||
      row.peopleInvolved !== "" ||
      row.actionTaken !== "" ||
      row.remarks !== "" ||
      row.status !== "" ||
      row.employee_name !== ""
    );
  };

  const shouldRequireInventoryFields = (row: InventoryFormRow) => {
    return (
      row.itemDescription !== "" ||
      row.criticalLevel !== "" ||
      row.quantity !== "" ||
      row.expiryDate !== ""
    );
  };

  const shouldRequireEfficiencyFields = (row: EfficiencyFormRow) => {
    return (
      row.shift !== "" ||
      row.accountable !== "" ||
      row.itemStockout !== "" ||
      row.itemDescription !== "" ||
      row.correctiveAction !== "" ||
      row.preventiveAction !== ""
    );
  };

  // Get content for daily event steps
  const getDailyStepContent = (step: number): React.ReactNode => {
    switch (step) {
      case 0:
        const clearAttendanceFields = () => {
          setAttendanceRows([
            {
              id: generateUniqueId(),
              store_code: selectedStore,
              attendance_date: "",
              shift: "",
              employee_name: "",
              attendance: "",
            },
          ]);
        };

        return (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "1.5rem",
                color: "black",
              }}
            >
              Attendance Form (Daily)
            </Typography>

            {attendanceRows.map((row, index) => {
              const isLeaveOrRestDay = row.attendance
                ? parseInt(row.attendance) >= 4 &&
                  parseInt(row.attendance) <= 16
                : false;

              return (
                <Paper key={row.id} elevation={1} sx={{ mb: 2, p: 2 }}>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Date"
                        name="attendance_date"
                        type="date"
                        required
                        value={row.attendance_date}
                        onChange={(e) => handleAttendanceInputChange(row.id, e)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        size="small"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControl
                        fullWidth
                        size="small"
                        required
                        error={!row.attendance}
                      >
                        <InputLabel>Attendance</InputLabel>
                        <Select
                          name="attendance"
                          value={row.attendance}
                          label="Attendance"
                          onChange={(e) =>
                            handleAttendanceSelectChange(row.id, e)
                          }
                          required
                        >
                          {attendance_status_data.map((status) => (
                            <MenuItem
                              key={status.id}
                              value={status.id.toString()}
                            >
                              {status.name || status.id || status.id.toString()}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small" disabled>
                        <InputLabel>Employee Name</InputLabel>
                        <Select
                          name="employee_name"
                          value={row.employee_name}
                          label="Employee Name"
                          inputProps={{ readOnly: true }}
                          required
                        >
                          {employee_data.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id.toString()}>
                              {emp.name || emp.id.toString()}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControl
                        fullWidth
                        size="small"
                        disabled={isLeaveOrRestDay}
                        required
                        error={!row.shift}
                      >
                        <InputLabel>Shift</InputLabel>
                        <Select
                          name="shift"
                          required
                          value={isLeaveOrRestDay ? "3" : row.shift}
                          label="Shift"
                          onChange={(e) =>
                            handleAttendanceSelectChange(row.id, e)
                          }
                          inputProps={{ readOnly: isLeaveOrRestDay }}
                        >
                          {attendance_shift_data.map((shift) => (
                            <MenuItem
                              key={shift.id}
                              value={shift.id.toString()}
                            >
                              {shift.name || shift.id || shift.id.toString()}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Box>
        );

      // Add cases 1-4 for incident, inventory, efficiency, and svr forms
      // For brevity, I'm not including all the form cases here, but they would follow
      // the same pattern as in your original code

      default:
        return "Unknown step";
    }
  };

  // Calendar rendering functions (kept the same as original)
  const renderDayCell = (day: CalendarDay, index: number) => {
    const isToday = day.date.toDateString() === new Date().toDateString();
    const isSelected =
      dateWasSelected &&
      day.date.toDateString() === selectedDate.toDateString();

    return (
      <Grid item xs={12 / 7} key={index}>
        <Box
          sx={{
            minHeight: "80px",
            p: 1,
            m: 0.5,
            border: isToday
              ? "2px solid #2196f3"
              : isSelected
              ? "2px solid #2196f3"
              : "1px solid #e0e0e0",
            backgroundColor:
              isToday && isSelected
                ? "#e3f2fd"
                : isToday
                ? "#f3e5f5"
                : isSelected
                ? "#e3f2fd"
                : day.isCurrentMonth
                ? "white"
                : "#f5f5f5",
            borderRadius: 1,
            cursor: "pointer",
            opacity: day.isCurrentMonth ? 1 : 0.6,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#f0f0f0",
              transform: "translateY(-1px)",
            },
          }}
          onClick={() => handleDateSelect(day.date)}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: isToday ? "bold" : "normal",
              color: isToday ? "#2196f3" : "#333",
              mb: 0.5,
            }}
          >
            {day.date.getDate()}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
            {day.events.map((event, idx) => (
              <Button
                key={idx}
                variant="contained"
                size="small"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  handleEventSelect(event);
                }}
                sx={{
                  py: 0.2,
                  minHeight: 0,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  backgroundColor:
                    event === "Daily"
                      ? "#800000"
                      : event === "Weekly"
                      ? "#0F9D58"
                      : "#DC0000",
                  "&:hover": {
                    backgroundColor:
                      event === "Daily"
                        ? "#600000"
                        : event === "Weekly"
                        ? "#0A7A45"
                        : "#A50000",
                  },
                }}
              >
                {event}
              </Button>
            ))}
          </Box>
        </Box>
      </Grid>
    );
  };

  const renderCalendar = () => {
    const days = getDaysForMonthView();

    return (
      <Box sx={{ width: "100%", mb: 4 }}>
        {/* Clean Header Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #2c3e50 0%, #3498db 100%)",
            borderRadius: 2,
            p: 4,
            mb: 3,
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "2.5rem",
              mb: 1,
            }}
          >
            Select a Date to Manage Forms
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Choose any date from the calendar below to view or create daily
            reports
          </Typography>
        </Box>

        {/* Statistics Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderLeft: "4px solid #800000",
                background: "#fff",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  color: "#800000",
                }}
              >
                {monthStats.dailyEvents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Daily Events This Month
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderLeft: "4px solid #0F9D58",
                background: "#fff",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  color: "#0F9D58",
                }}
              >
                {monthStats.weeklyEvents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Weekly Events This Month
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderLeft: "4px solid #DC0000",
                background: "#fff",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  color: "#DC0000",
                }}
              >
                {monthStats.monthlyEvents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monthly Events This Month
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: "center",
                borderLeft: "4px solid #2196F3",
                background: "#fff",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  color: "#2196F3",
                }}
              >
                {todayStats.totalEvents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Today's Available Events
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Calendar */}
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          {/* Calendar Header */}
          <Box
            sx={{
              backgroundColor: "#f8f9fa",
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #dee2e6",
            }}
          >
            <Button
              onClick={goToPreviousMonth}
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthIcon />}
            >
              Previous
            </Button>

            <Typography
              variant="h5"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                color: "#2c3e50",
                fontSize: "1.8rem",
              }}
            >
              {currentMonth.toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </Typography>

            <Button
              onClick={goToNextMonth}
              variant="outlined"
              size="small"
              endIcon={<DateRangeIcon />}
            >
              Next
            </Button>
          </Box>

          {/* Calendar Grid */}
          <Box sx={{ p: 3, backgroundColor: "#fafbfc" }}>
            {/* Day Headers */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {dayNames.map((day, index) => (
                <Grid item xs={12 / 7} key={index}>
                  <Box
                    sx={{
                      py: 1.5,
                      backgroundColor: "#e9ecef",
                      borderRadius: 1,
                      textAlign: "center",
                      border: "1px solid #dee2e6",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "#495057",
                        fontSize: "0.85rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {day}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Calendar Days Grid */}
            <Grid container spacing={1}>
              {days.map((day, index) => {
                const isToday =
                  day.date.toDateString() === new Date().toDateString();
                const isSelected =
                  dateWasSelected &&
                  day.date.toDateString() === selectedDate.toDateString();

                return (
                  <Grid item xs={12 / 7} key={index}>
                    <Paper
                      elevation={isSelected ? 4 : 1}
                      sx={{
                        minHeight: "120px",
                        p: 2,
                        cursor: "pointer",
                        backgroundColor:
                          isToday && isSelected
                            ? "#e3f2fd"
                            : isToday
                            ? "#fff3e0"
                            : isSelected
                            ? "#e8f5e9"
                            : day.isCurrentMonth
                            ? "white"
                            : "#f8f9fa",
                        border: isToday
                          ? "2px solid #ff9800"
                          : isSelected
                          ? "2px solid #4caf50"
                          : "1px solid #e0e0e0",
                        borderRadius: 2,
                        transition: "all 0.2s ease-in-out",
                        opacity: day.isCurrentMonth ? 1 : 0.7,
                        "&:hover": {
                          backgroundColor: isSelected ? "#e8f5e9" : "#f5f5f5",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      }}
                      onClick={() => handleDateSelect(day.date)}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: isToday ? 700 : 500,
                          color: isToday
                            ? "#ff9800"
                            : isSelected
                            ? "#4caf50"
                            : day.isCurrentMonth
                            ? "#333"
                            : "#999",
                          mb: 1,
                          fontSize: "1.1rem",
                        }}
                      >
                        {day.date.getDate()}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        {day.events.map((event, idx) => (
                          <Button
                            key={idx}
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventSelect(event);
                            }}
                            sx={{
                              py: 0.5,
                              px: 1,
                              minHeight: "28px",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              borderRadius: 1.5,
                              textTransform: "none",
                              backgroundColor:
                                event === "Daily"
                                  ? "#800000"
                                  : event === "Weekly"
                                  ? "#0F9D58"
                                  : "#DC0000",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              "&:hover": {
                                backgroundColor:
                                  event === "Daily"
                                    ? "#600000"
                                    : event === "Weekly"
                                    ? "#0A7A45"
                                    : "#A50000",
                                transform: "scale(1.02)",
                              },
                            }}
                          >
                            {event}
                          </Button>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Legend */}
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f8f9fa",
              borderTop: "1px solid #dee2e6",
            }}
          >
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Event Types:
                </Typography>
              </Grid>
              <Grid item>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: "#800000",
                      borderRadius: "50%",
                    }}
                  />
                  <Typography variant="body2">Daily</Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: "#0F9D58",
                      borderRadius: "50%",
                    }}
                  />
                  <Typography variant="body2">Weekly</Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: "#DC0000",
                      borderRadius: "50%",
                    }}
                  />
                  <Typography variant="body2">Monthly</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    );
  };

  // Get form content based on selected event type
  const getEventFormContent = () => {
    switch (selectedEvent) {
      case "Daily":
        return (
          <Box>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {getCurrentSteps().map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {getStepContent(activeStep)}
          </Box>
        );
      case "Weekly":
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Weekly reports will be available in the future.
            </Typography>
            <Typography variant="body1">
              This feature is currently under development. Please check back
              later.
            </Typography>
          </Box>
        );
      case "Monthly":
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Monthly reports will be available in the future.
            </Typography>
            <Typography variant="body1">
              This feature is currently under development. Please check back
              later.
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Box
        sx={{
          backgroundColor: "#fff",
          borderBottom: "3px solid #800000",
          p: 3,
          mb: 3,
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: "2.5rem",
            color: "#2c3e50",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <EditCalendarIcon
            sx={{
              mr: 2,
              color: "#800000",
              fontSize: "2.5rem",
            }}
          />
          6MS FORMS CALENDAR SYSTEM
        </Typography>
      </Box>

      {/* Store Selector */}
      {renderStoreSelector()}

      {/* Conditional rendering based on store selection */}
      {!selectedStore && stores.length > 1 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Please select a store to continue
          </Typography>
        </Box>
      ) : !showForms && !isLoading ? (
        renderCalendar()
      ) : isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setShowForms(false);
                setSelectedEvent(null);
                window.location.reload();
              }}
              sx={{ mr: 2 }}
            >
              Back to Calendar
            </Button>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "1.7rem",
                color: "black",
              }}
            >
              {selectedEvent} Event - {selectedDate.toLocaleDateString()}
            </Typography>
            <Box sx={{ marginLeft: "auto" }}>
              <Button variant="outlined" onClick={goToViewPage}>
                View All Records
              </Button>
            </Box>
          </Box>

          {getEventFormContent()}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              onClick={() => {
                setShowForms(false);
                window.location.reload();
              }}
              variant="outlined"
            >
              Cancel
            </Button>

            <Box>
              {activeStep > 0 && selectedEvent === "Daily" ? (
                <Button onClick={handleBack} sx={{ mr: 1 }}>
                  Back
                </Button>
              ) : null}

              {activeStep < getCurrentSteps().length - 1 &&
              selectedEvent === "Daily" ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={openConfirmationDialog}
                >
                  Submit
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 0,
            minWidth: "600px",
            maxHeight: "80vh",
            overflowY: "auto",
            boxShadow: 3,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            transition: "transform 0.3s ease-in-out",
            transform: confirmDialogOpen ? "scale(1)" : "scale(0.95)",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ bgcolor: "primary.main", color: "white", fontWeight: "bold" }}
        >
          Confirm Submission
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {renderConfirmationDialogContent()}
        </DialogContent>
        <DialogActions
          sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "grey.500",
              color: "grey.700",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmedSubmit}
            autoFocus
            variant="contained"
            color="primary"
            sx={{
              bgcolor: "primary.dark",
              "&:hover": { bgcolor: "primary.main" },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Records Dialog */}
      <Dialog
        open={viewRecordsDialogOpen}
        onClose={() => setViewRecordsDialogOpen(false)}
        aria-labelledby="view-records-dialog-title"
        aria-describedby="view-records-dialog-description"
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            minWidth: { xs: "95%", sm: "450px", md: "550px" },
            maxHeight: "80vh",
            overflowY: "auto",
            backgroundColor: "#ffffff",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            transition: "all 0.3s ease-in-out",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <DialogTitle
            id="view-records-dialog-title"
            sx={{
              bgcolor: "primary.main",
              color: "white",
              py: 2.5,
              pl: 3,
              pr: 6,
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
              fontWeight: 500,
              letterSpacing: "0.5px",
            }}
          >
            View Records
          </DialogTitle>

          <IconButton
            aria-label="close"
            onClick={() => setViewRecordsDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "white",
              opacity: 0.8,
              "&:hover": {
                opacity: 1,
                background: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, py: 4 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <LaunchIcon sx={{ fontSize: 28, color: "primary.main", mr: 1.5 }} />
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 500, color: "#424242" }}
            >
              External Navigation
            </Typography>
          </Box>

          <DialogContentText
            id="view-records-dialog-description"
            sx={{
              color: "#616161",
              fontSize: "1rem",
              lineHeight: 1.6,
              mb: 1,
              pl: 0.5,
            }}
          >
            To view all records, a new tab needs to be opened. Continue?
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: "#f8f8f8",
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setViewRecordsDialogOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "grey.500",
              color: "grey.700",
              px: 3,
              py: 1,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleViewRecordsConfirm}
            variant="contained"
            color="primary"
            startIcon={<LaunchIcon />}
            sx={{
              bgcolor: "primary.dark",
              color: "white",
              px: 3,
              py: 1,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { bgcolor: "primary.main" },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <Dialog
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 2,
            minWidth: { xs: "95%", sm: "400px", md: "450px" },
            backgroundColor: "#ffffff",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            transition: "all 0.3s ease-in-out",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <DialogTitle
            id="alert-dialog-title"
            sx={{
              bgcolor: "error.main",
              color: "white",
              py: 2,
              pl: 3,
              pr: 6,
              fontSize: { xs: "1.1rem", sm: "1.2rem" },
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <ErrorOutlineIcon />
            {alertTitle}
          </DialogTitle>

          <IconButton
            aria-label="close"
            onClick={() => setAlertOpen(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "white",
              opacity: 0.8,
              "&:hover": {
                opacity: 1,
                background: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <DialogContentText
            id="alert-dialog-description"
            sx={{
              color: "#616161",
              fontSize: "1rem",
              lineHeight: 1.6,
              mb: 1,
            }}
          >
            {alertMessage}
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "#f8f8f8",
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={() => setAlertOpen(false)}
            variant="contained"
            color="primary"
            sx={{
              bgcolor: "primary.dark",
              color: "white",
              px: 4,
              py: 1,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { bgcolor: "primary.main" },
            }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FormList;
